import { useMemo, useState, type FormEvent } from 'react';
import { kennzeichenSchema } from '@fastgarage/protocol';

export interface StartFormProps {
  consentText: string;
  /** Download path of the Android app, or null when none is deployed. */
  androidApkUrl: string | null;
  androidApkSizeBytes: number | null;
  /** Legal pages; each link is hidden while its URL is not configured. */
  impressumUrl: string | null;
  datenschutzUrl: string | null;
  kennzeichen: string;
  onKennzeichenChange(value: string): void;
  busy: boolean;
  error: string | null;
  /** Hidden on the SMS-link entry ('/s/:linkToken'). */
  showCodeEntry: boolean;
  /** joinCode === null → primary start button; otherwise join by code. */
  onSubmit(joinCode: string | null): void;
}

/** " (APK, 24 MB)" — omitted entirely when the size is unknown. */
function formatApkSize(bytes: number | null): string {
  if (bytes === null || bytes <= 0) return ' (APK)';
  const mb = bytes / (1024 * 1024);
  const value = mb >= 10 ? Math.round(mb).toString() : mb.toFixed(1).replace('.', ',');
  return ` (APK, ${value} MB)`;
}

/**
 * Start page: Kennzeichen (German plate look, pure CSS), consent checkbox and
 * the primary start button. No camera or video element exists on this screen.
 */
export function StartForm(props: StartFormProps): JSX.Element {
  const [consent, setConsent] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);
  const [codeValue, setCodeValue] = useState('');

  const kennzeichenValid = useMemo(
    () => kennzeichenSchema.safeParse(props.kennzeichen).success,
    [props.kennzeichen],
  );
  const baseValid = kennzeichenValid && consent && !props.busy;
  const codeValid = /^\d{6}$/.test(codeValue);

  function handlePrimarySubmit(event: FormEvent): void {
    event.preventDefault();
    if (baseValid) props.onSubmit(null);
  }

  return (
    <div className="start-page">
      <header className="start-header">
        <span className="brand">
          Fast<span className="brand-accent">garage</span>
        </span>
        <h1>Video-Schadenaufnahme</h1>
        <p className="start-intro">
          Geben Sie Ihr Kennzeichen ein und starten Sie den Video-Termin direkt im Browser — ohne
          App.
        </p>
      </header>

      <form className="start-form" onSubmit={handlePrimarySubmit}>
        <label className="field-label" htmlFor="kennzeichen-input">
          Ihr Kennzeichen
        </label>
        <div className="plate" aria-hidden={false}>
          <div className="plate-band">
            <span className="plate-stars" aria-hidden="true">
              ★
            </span>
            <span className="plate-d">D</span>
          </div>
          <input
            id="kennzeichen-input"
            className="plate-input"
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            maxLength={20}
            placeholder="AC FG 1234"
            value={props.kennzeichen}
            onChange={(e) => props.onKennzeichenChange(e.target.value.toUpperCase())}
          />
        </div>

        <label className="consent">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span>{props.consentText}</span>
        </label>

        {props.error ? (
          <p className="form-error" role="alert">
            {props.error}
          </p>
        ) : null}

        <button type="submit" className="btn-primary" disabled={!baseValid}>
          {props.busy ? 'Wird gestartet…' : 'Video-Termin starten'}
        </button>
      </form>

      {props.showCodeEntry ? (
        <div className="code-entry-block">
          <button
            type="button"
            className="btn-link"
            aria-expanded={codeOpen}
            onClick={() => setCodeOpen((open) => !open)}
          >
            Ich habe einen Code
          </button>
          {codeOpen ? (
            <div className="code-entry">
              <label className="field-label" htmlFor="code-input">
                6-stelliger Code aus der Werkstatt
              </label>
              <input
                id="code-input"
                className="code-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={codeValue}
                onChange={(e) => setCodeValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
              <button
                type="button"
                className="btn-secondary"
                disabled={!baseValid || !codeValid}
                onClick={() => props.onSubmit(codeValue)}
              >
                Mit Code beitreten
              </button>
              <p className="code-entry-hilfe">
                Kennzeichen und Einwilligung oben werden auch für den Code-Beitritt benötigt.
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      {props.androidApkUrl ? (
        <section className="apk-block">
          <h2>Kamera per USB am Handy?</h2>
          <p>
            Browser auf Android können keine USB-Kameras verwenden. Für diesen Fall gibt es unsere
            Android-App — sie überträgt das Bild der angeschlossenen USB-Kamera an die Werkstatt.
          </p>
          <a className="btn-secondary apk-download" href={props.androidApkUrl} download>
            Android-App herunterladen{formatApkSize(props.androidApkSizeBytes)}
          </a>
          <p className="apk-hinweis">
            Nur für Sonderfälle nötig. Beim Öffnen der Datei muss die Installation aus unbekannter
            Quelle einmalig erlaubt werden. Für iPhones ist keine App verfügbar.
          </p>
        </section>
      ) : null}

      <footer className="start-footer">
        <p>Es werden ausschließlich Einzelfotos gespeichert — keine Videoaufzeichnung.</p>
        {props.datenschutzUrl || props.impressumUrl ? (
          <p className="legal-links">
            {props.datenschutzUrl ? (
              <a href={props.datenschutzUrl} target="_blank" rel="noopener noreferrer">
                Datenschutz
              </a>
            ) : null}
            {props.impressumUrl ? (
              <a href={props.impressumUrl} target="_blank" rel="noopener noreferrer">
                Impressum
              </a>
            ) : null}
          </p>
        ) : null}
      </footer>
    </div>
  );
}
